import { Component, DOCUMENT, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { AuthService } from '@core/authentication';
import { environment } from '@env/environment';
import { ImageFallbackDirective } from '@shared/directives/image-fallback.directive';

import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';
import { DeployVersionInfo, DeployVersionService } from '@shared/services/deploy-version.service';
import { StringUtils } from '@shared/utils/stringUtils';
import { defaultAppName, defaultCopyright, defaultVersion } from 'app/constants/defaults';

@Component({
  selector: 'app-version-dialog',
  templateUrl: './version-dialog.component.html',
  styleUrls: ['./version-dialog.component.scss'],
  imports: [
    MatDialogContent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    ImageFallbackDirective,
  ],
})
export class VersionDialogComponent implements OnInit, OnDestroy {
  public version = '';
  public copyright = '';
  public appName = '';
  public frontendDeployVersion: DeployVersionInfo | null = null;
  public backendDeployVersion: DeployVersionInfo | null = null;
  public backendDeployUnavailable = false;
  public canViewDeployInfo = false;
  private deployVersionsLoaded = false;
  private readonly _subs: Subscription[] = [];
  private observer: MutationObserver | undefined;

  public readonly sidebarLogoSrc = StringUtils.brandResourceWithFallback('sidebarLogo');
  public readonly sidebarLogoFallback = StringUtils.brandResourceFallbackPath('sidebarLogo');
  public readonly brandLogoPath = StringUtils.brandResource('logo');
  public readonly brandLogoPathLight = StringUtils.brandResource('logoLight');
  public brandLogo: string | undefined;
  public readonly brandLogoWidth = environment.logoWidthPx ?? '220px';

  constructor(
    private readonly _metadataService: MetadataService,
    private readonly _deployVersionService: DeployVersionService,
    private readonly _authService: AuthService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  get isDarkMode(): boolean {
    return this.document.documentElement.classList.contains('theme-dark');
  }

  ngOnInit(): void {
    this._subs.push(
      this._authService.user().subscribe(user => {
        this.canViewDeployInfo = user.roles?.includes('admin') ?? false;
        if (this.canViewDeployInfo && !this.deployVersionsLoaded) {
          this.loadDeployVersions();
        }
      })
    );

    this.updateLogo();

    this.observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          this.updateLogo();
        }
      });
    });

    this.observer.observe(this.document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.Version).subscribe({
        next: metadata => {
          if (metadata.value) {
            this.version = metadata.value;
          } else {
            this.version = defaultVersion;
          }
        },
        error: () => {
          this.version = defaultVersion;
        },
      })
    );
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.Copyright).subscribe({
        next: metadata => {
          if (metadata.value) {
            this.copyright = metadata.value;
          } else {
            this.copyright = defaultCopyright;
          }
        },
        error: () => {
          this.copyright = defaultCopyright;
        },
      })
    );
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.AppName).subscribe({
        next: metadata => {
          if (metadata.value) {
            this.appName = metadata.value;
          } else {
            this.appName = defaultAppName;
          }
        },
        error: () => {
          this.appName = defaultAppName;
        },
      })
    );
  }

  public ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private updateLogo(): void {
    this.brandLogo = this.isDarkMode ? this.brandLogoPath : this.brandLogoPathLight;
  }

  formatBuiltAt(isoDate: string): string {
    return this._deployVersionService.formatBuiltAt(isoDate);
  }

  private loadDeployVersions(): void {
    this.deployVersionsLoaded = true;

    this._subs.push(
      this._deployVersionService.loadFrontendVersion().subscribe({
        next: version => {
          this.frontendDeployVersion = version;
        },
      })
    );

    this._subs.push(
      this._deployVersionService.getBackendVersion().subscribe({
        next: version => {
          this.backendDeployVersion = version;
          this.backendDeployUnavailable = !version;
        },
        error: () => {
          this.backendDeployUnavailable = true;
        },
      })
    );
  }
}
